import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { Currency } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type {
  AppleTouchIcons,
  Language,
  Website,
  WebsiteConfig,
  WebsiteLanguages
} from '@ps/web-martech-cms-types';
import { invert } from 'lodash';
import type { Selector } from 'reselect';

import type { SystemState } from '../../../index';
import { select } from '../../../index';
import type { RootState, SelectorCreator, Slicer } from '../../types';

import type { CMSState } from './cms';

type SiteSuffixMap = { [key in Sites]: string };

export interface RequestMeta {
  locale: string;
  currency: Currency;
  systemState: SystemState;
  site?: Sites;
  brand?: BrandTypes;
}

const ExternalPartnerSites = [
  Sites.IT_SISAL_EXT,
  Sites.IT_PS_EXT,
  Sites.UK_PS_EXT,
  Sites.COM_PS_EXT,
  Sites.DE_PS_EXT,
  Sites.EE_PS_EXT,
  Sites.ES_PS_EXT,
  Sites.SE_PS_EXT,
  Sites.DK_PS_EXT,
  Sites.RO_PS_EXT,
  Sites.BE_PS_EXT,
  Sites.BG_PS_EXT,
  Sites.GR_PS_EXT,
  Sites.NL_PS_EXT,
  Sites.CH_PS_EXT,
  Sites.IT_SNAI_EXT,
  Sites.PT_PS_EXT,
  Sites.FR_PS_EXT
];

const BrandCodes = {
  [BrandTypes.POKER_STARS]: 'PS',
  [BrandTypes.STARS_CASINO]: 'SC',
  [BrandTypes.BET_STARS]: 'BS',
  [BrandTypes.FULL_TILT]: 'FT',
  [BrandTypes.POKER_STARS_VEGAS]: 'VS',
  [BrandTypes.FOX_BET]: 'FXB'
};

const BrandDisplayNames: Partial<Record<BrandTypes, string>> = {
  [BrandTypes.POKER_STARS]: 'PokerStars',
  [BrandTypes.STARS_CASINO]: 'PokerStars Casino',
  [BrandTypes.BET_STARS]: 'PokerStars Sports',
  [BrandTypes.POKER_STARS_VEGAS]: 'PokerStars Vegas'
};

const BrandsToBrandTypes: Partial<{ [key in Website['brand']]: keyof typeof BrandTypes }> = {
  BetStars: 'BET_STARS',
  FoxBet: 'FOX_BET',
  FullTilt: 'FULL_TILT',
  PokerStars: 'POKER_STARS',
  PokerStarsCasino: 'STARS_CASINO',
  PokerStarsVegas: 'POKER_STARS_VEGAS',
  Unknown: 'UNKNOWN'
};

const SitesToPMFSites: Partial<{ [key in Website['site']]: keyof typeof Sites }> = {
  BE: 'BEL',
  BG: 'BUL',
  CAON: 'CA_ON',
  CZ: 'CZE',
  DESH: 'DE_SH',
  DK: 'DEN',
  EE: 'EST',
  ES: 'SPA',
  FR: 'FRA',
  IN: 'IND',
  IT: 'ITA',
  PT: 'POR',
  RO: 'ROM',
  SE: 'SWE',
  SISAL: 'IT_SISAL_EXT',
  USCO: 'US_CO',
  USNJ: 'US_NJ',
  USPA: 'US_PA'
};

const selectors = (slice: Slicer<CMSState>, createSelector: SelectorCreator) => ({
  getAlternateLinks() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) =>
        (select.location.stripPrefix as Selector<RootState, string>)(rootState),
      (website, strippedPathname) =>
        website?.alternate_links
          ?.filter(({ link: { excluded_paths: excludedPaths } }) => {
            if (excludedPaths?.length) {
              const joinedExcludedPaths = excludedPaths
                .map((ep) => ep.path.replace(/(^\/)|(\/$)/g, ''))
                .join('|');
              const startWithExcludedPath = new RegExp(`^/(${joinedExcludedPaths})/.*`, 'g');
              return !startWithExcludedPath.test(strippedPathname);
            }

            return true;
          })
          // eslint-disable-next-line @typescript-eslint/naming-convention
          ?.map(({ link: { locale_code: localeCode, base_url: baseUrl, replaced_path_start } }) => {
            const linkPath =
              replaced_path_start?.path_start_to_replace && replaced_path_start?.new_path_start
                ? strippedPathname.replace(
                    new RegExp(`^${replaced_path_start.path_start_to_replace}`),
                    replaced_path_start.new_path_start
                  )
                : strippedPathname;

            return {
              localeCode,
              url: `${baseUrl}${linkPath}`
            };
          })
    );
  },
  getWebSiteBrandName() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (website) => website?.brand || 'UNKNOWN'
    );
  },
  getWebsiteBrandId() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebSiteBrandName(rootState),
      (websiteBrand) =>
        BrandTypes[websiteBrand as keyof typeof BrandTypes] ??
        BrandTypes[BrandsToBrandTypes[websiteBrand] as keyof typeof BrandTypes]
    );
  },
  getPathBrandId() {
    return createSelector(
      (rootState: RootState, pathname: string | undefined) => pathname,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.location.stripBaseURL(rootState, undefined),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (pathname, websiteBrandId, locationPathname, siteId) => {
        const path = pathname ?? locationPathname;
        if (
          [
            BrandTypes.BET_STARS,
            BrandTypes.POKER_STARS,
            BrandTypes.STARS_CASINO,
            BrandTypes.POKER_STARS_VEGAS
          ].includes(websiteBrandId)
        ) {
          switch (true) {
            case path.startsWith('/poker/'):
              return BrandTypes.POKER_STARS;
            case path.startsWith('/vegas/') && (siteId === Sites.DE || siteId === Sites.DE_SH):
              return BrandTypes.STARS_CASINO;
            case path.startsWith('/vegas/'):
              return BrandTypes.POKER_STARS_VEGAS;
            case path.startsWith('/casino/'):
              return BrandTypes.STARS_CASINO;
            case path.startsWith('/sports/'):
              return BrandTypes.BET_STARS;
          }
        }
        return websiteBrandId;
      }
    );
  },
  getBrandId() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      ({ pathBrand }, websiteBrandId) => pathBrand || websiteBrandId
    );
  },
  getPathBrandName() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      ({ pathBrand }) => BrandTypes[pathBrand as BrandTypes] || BrandTypes[BrandTypes.POKER_STARS]
    );
  },
  getBrandName(this: { getBrandId: Selector<RootState, BrandTypes> }) {
    return createSelector(
      this.getBrandId,
      (brandId) => BrandDisplayNames[brandId] ?? invert(BrandTypes)[brandId]
    );
  },
  getBrandCode() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brandId) => BrandCodes[brandId as keyof typeof BrandCodes]
    );
  },
  getCanonicalLink() {
    return slice((state) => state.website?.canonical_link);
  },
  getContent() {
    return slice(({ content }) => content);
  },
  getContentItem() {
    return createSelector(
      (rootState: RootState) => select.cms.getContent(rootState),
      (_rootState: RootState, uid: string) => uid,
      (content, uid) => content[uid]
    );
  },
  getDomain() {
    return createSelector(
      slice,
      (rootState: RootState) => select.location.getHostname(rootState),
      ({ website }, hostname) =>
        (website?.domains?.find((domain) => hostname.startsWith(domain)) ||
          website?.domains?.[0]) as string
    );
  },
  getLegalAge() {
    return slice(({ website }) => website?.legal_age);
  },
  getNotificationSubscriptionPath() {
    return createSelector(
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.device.getId(rootState),
      (rootState: RootState) => select.account.getLicense(rootState),
      (rootState: RootState) => select.account.getUserId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (locale, device, license, userId, site) =>
        `/user/queue/starsweb/notifications/${locale}/${device}/${license}/${site}/${userId}`
    );
  },
  getFaviconURL() {
    return slice((state) => state.website?.favicon?.url);
  },
  getAppleTouchIcons() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (website, content) => {
        const refs = website?.apple_touch_icons;
        return refs?.length ? (content[refs[0].uid] as AppleTouchIcons) : undefined;
      }
    );
  },
  getRegion() {
    return slice((state) => state.website?.region || undefined);
  },
  /**
   * @deprecated For backported API compatibility only. Use `bindRequest` instead.
   */
  getRequestOptions() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.cms.getRegion(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => select.device.getPlatform(rootState),
      (rootState: RootState) => select.device.get(rootState),
      (rootState: RootState) => select.currency.get(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.session.getWMTAuthorization(rootState),
      (rootState: RootState) => select.location.getHref(rootState),
      (rootState: RootState) => select.location.getOrigin(rootState),
      (rootState: RootState) => select.location.getHost(rootState),
      (rootState: RootState) => select.system.get(rootState),
      (
        website,
        websiteBrand,
        site,
        region,
        country,
        platform,
        { device, os, embedded },
        currency,
        locale,
        authorization,
        href,
        origin,
        host,
        systemState
      ) => {
        const requestMeta: RequestMeta = { locale, currency, systemState };
        const headers: Record<string, string> = {
          referer: href,
          ['x-fi-ps-platform-id']: `${platform}`
        };

        if (!canUseDOM) {
          if (website) Object.assign(requestMeta, { site, brand: websiteBrand });

          Object.assign(requestMeta, {
            device,
            country,
            os,
            embedded: embedded ? 1 : 0
          });
          headers['x-forwarded-host'] = host;
          headers.origin = origin;
          if (region) (requestMeta as typeof requestMeta & { region: string }).region = region;
        }

        if (authorization) headers.authorization = authorization;
        return { meta: requestMeta, headers };
      }
    );
  },
  getAliases() {
    return slice((state) => state.aliases || {});
  },
  isAliasDomain() {
    return createSelector(
      (rootState: RootState) => select.cms.getDomain(rootState),
      (domain) => /[a-z]+.pokerstars-\d+.[a-z]+/.test(domain)
    );
  },
  getSiteId() {
    return slice((state) =>
      state.website?.site
        ? Sites[state.website.site as keyof typeof Sites] ??
          Sites[SitesToPMFSites[state.website.site] as keyof typeof Sites] ??
          Sites.COM
        : Sites.COM
    );
  },
  getSiteName() {
    return slice((state) => state.website?.site || 'COM');
  },
  getStarsDomain() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (id: number) =>
        ({
          [Sites.ITA]: 'starsaccount.it',
          [Sites.GR]: 'starsaccount.gr',
          [Sites.FRA]: 'starsaccount.fr',
          [Sites.SPA]: 'starsaccount.es',
          [Sites.DEN]: 'starsaccount.dk',
          [Sites.EU]: 'starsaccount.eu',
          [Sites.UK]: 'starsaccount.uk',
          [Sites.BUL]: 'starsaccount.bg',
          [Sites.DE_SH]: 'sh.starsaccount.de',
          [Sites.ROM]: 'starsaccount.ro',
          [Sites.EST]: 'starsaccount.ee',
          [Sites.CZE]: 'starsaccount.cz',
          [Sites.BEL]: 'starsaccount.be',
          [Sites.RUSOPM]: 'starsaccount.net',
          [Sites.POR]: 'starsaccount.pt',
          [Sites.SWE]: 'starsaccount.se',
          [Sites.NET]: 'starsaccount.net',
          [Sites.US_PA]: 'starsaccountpa.com',
          [Sites.US_NJ]: 'pokerstarscasinonj.com',
          [Sites.USMI]: 'pokerstarscasinomi.com',
          [Sites.NL]: 'pokerstars.nl'
        }[id] || 'starsaccount.com')
    );
  },
  getToken() {
    return createSelector(
      slice,
      (_rootState: RootState, token: string) => token,
      ({ website }, token) => website?.tokens.find(({ key }) => key === token)?.value
    );
  },
  getCashierHref() {
    const siteSuffixMap: Partial<SiteSuffixMap> = {
      [Sites.COM]: '',
      [Sites.NET]: '',
      [Sites.ITA]: 'it',
      [Sites.FRA]: '',
      [Sites.EST]: 'ee',
      [Sites.BEL]: 'be',
      [Sites.SPA]: 'es',
      [Sites.DEN]: 'dk',
      [Sites.EU]: 'eu',
      [Sites.US_NJ]: 'usnj',
      [Sites.UK]: 'uk',
      [Sites.BUL]: 'bg',
      [Sites.DE_SH]: '',
      [Sites.ROM]: 'ro',
      [Sites.POR]: 'pt',
      [Sites.CZE]: 'cz',
      [Sites.RUSO]: '',
      [Sites.RUSOPM]: '',
      [Sites.IND]: '',
      [Sites.ASIA]: '',
      [Sites.RU]: '',
      [Sites.US_PA]: '',
      [Sites.SWE]: 'se',
      [Sites.CH]: '',
      [Sites.ARBA]: '',
      [Sites.US_CO]: 'uspa',
      [Sites.DE]: '',
      [Sites.USMI]: 'uspa',
      [Sites.NL]: '',
      [Sites.CA_ON]: '',
      [Sites.GR]: ''
    };

    return createSelector(
      (rootState: RootState) => select.device.isEmbedded(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (isEmbedded, site) =>
        isEmbedded ? `pokerstars${siteSuffixMap[site]}://cashier/open` : undefined
    );
  },
  getWebsite() {
    return slice(({ website }) => website);
  },
  getExcludedWebsiteLanguages() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (website, content, country) => {
        const refs = website?.website_languages;
        const websiteLanguages =
          !!refs?.length &&
          (content[refs[0].uid] as Omit<WebsiteLanguages, 'excluded_languages'> & {
            excluded_languages?: WebsiteLanguages['excluded_languages'];
          });

        if (!websiteLanguages) return [];

        return websiteLanguages.excluded_languages?.map((item) => {
          const locale = content[item.language[0].uid] as Language;
          const countries = item.visibility_rules.countries
            .split(',')
            .map((country: string) => country.trim());
          const isRestricted = country && countries.includes(country);

          if (isRestricted && item.visibility_rules.operator === '!=') {
            return locale.short_code;
          }
        });
      }
    );
  },
  getWebsiteLanguages() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (rootState: RootState) => select.cms.getExcludedWebsiteLanguages(rootState),
      (website, content, excludedLanguages) => {
        const refs = website?.website_languages;
        const websiteLanguages = !!refs?.length && (content[refs[0].uid] as WebsiteLanguages);
        if (!websiteLanguages) return [];

        return websiteLanguages.languages
          .map((item) => (content[item.uid] as Language).short_code)
          .filter((item) => !excludedLanguages?.includes(item));
      }
    );
  },
  getWebsiteLaunchDate() {
    return slice(({ website, content }) => {
      const refs = website?.website_config;
      const websiteConfig = !!refs?.length && (content[refs[0].uid] as WebsiteConfig);
      return websiteConfig ? websiteConfig.isp_launch_date : undefined;
    });
  },
  getWebsiteConfig() {
    return createSelector(
      (_rootState: RootState, configKey: string) => configKey,
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (rootState: RootState) => select.cms.getContent(rootState),
      (configKey, website, content) => {
        const refs = website?.website_config;
        const websiteConfig = !!refs?.length && (content[refs[0].uid] as WebsiteConfig);
        return websiteConfig ? websiteConfig[configKey as keyof WebsiteConfig] : undefined;
      }
    ) as (state: RootState, key: string) => WebsiteConfig[keyof WebsiteConfig] | undefined;
  },
  isAuthRequired() {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (pathname, website) => website?.auth_required_paths?.some((path) => pathname.includes(path))
    );
  },
  isSiteDE() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => site === Sites.DE
    );
  },
  isSiteUS() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => [Sites.USMI, Sites.US_NJ, Sites.US_PA, Sites.US_CO].includes(site)
    );
  },
  isSiteUSPA() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => [Sites.US_PA].includes(site)
    );
  },
  showOntarioContent(this: { getSiteId: Selector<RootState, Sites> }) {
    return createSelector(this.getSiteId, (site) => site === Sites.CA_ON);
  },
  getRedirectUrl() {
    return createSelector(
      (rootState: RootState) => select.localSettings.get(rootState),
      ({ redirectTo }) => redirectTo || '/'
    );
  },
  isSportsBrand: () =>
    createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brand) => brand === BrandTypes.BET_STARS
    ),
  isIspRouting: () =>
    createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPRouting),
      (rootState: RootState) => select.location.getQuery(rootState),
      (ispRouting, { siteId }) => ispRouting || siteId === 'isp'
    ),
  isExternalPartnerSite(this: { getSiteId: Selector<RootState, Sites> }) {
    return createSelector(this.getSiteId, (siteId) => ExternalPartnerSites.includes(siteId));
  },
  getPathBrand() {
    return slice((state) => state.pathBrand);
  }
});

export default selectors;
